import { useSyncExternalStore } from 'react'
import type { Ctx, CopilotHistoryTurn } from './brain'
import type { CopilotLaunch } from './launcher'

export interface CopilotMessage {
  id: string
  role: 'user' | 'assistant' | 'error'
  content: string
}

export interface CopilotConversationState {
  messages: readonly CopilotMessage[]
  contextSnapshot: Ctx | null
  pendingLaunch: CopilotLaunch | null
  busy: boolean
  draft: string
}

let sequence = 0
let state: CopilotConversationState = {
  messages: [], contextSnapshot: null, pendingLaunch: null, busy: false, draft: '',
}
const listeners = new Set<() => void>()
const publish = (patch: Partial<CopilotConversationState>) => {
  state = { ...state, ...patch }
  listeners.forEach((listener) => listener())
}

export function queueCopilotLaunch(request: CopilotLaunch) {
  publish({
    pendingLaunch: request,
    draft: request.query,
    // A mounted conversation owns its original scope. A new one captures the
    // request. Navigating elsewhere or launching another query cannot mutate it.
    contextSnapshot: state.messages.length === 0 ? request.contextSnapshot : state.contextSnapshot,
  })
}

export function consumeCopilotLaunch(id: string) {
  if (state.pendingLaunch?.id === id) publish({ pendingLaunch: null })
}

export function setCopilotDraft(draft: string) { publish({ draft }) }
export function setCopilotBusy(busy: boolean) { publish({ busy }) }

export function addCopilotMessage(role: CopilotMessage['role'], content: string) {
  publish({ messages: [...state.messages, { id: `turn-${++sequence}`, role, content }] })
}

export function newCopilotConversation(context: Ctx | null = null) {
  publish({ messages: [], contextSnapshot: context, pendingLaunch: null, busy: false, draft: '' })
}

/** Last six completed messages, with a hard character ceiling at the client seam. */
export function boundedCopilotHistory(messages = state.messages): CopilotHistoryTurn[] {
  const selected = messages.filter((message) => message.role !== 'error').slice(-6)
  let budget = 6_000
  const answer: CopilotHistoryTurn[] = []
  for (const message of selected) {
    if (budget <= 0) break
    const content = message.content.slice(0, budget)
    answer.push({ role: message.role === 'user' ? 'user' : 'assistant', content })
    budget -= content.length
  }
  return answer
}

export function getCopilotConversation() { return state }

export function useCopilotConversation() {
  return useSyncExternalStore(
    (listener) => { listeners.add(listener); return () => listeners.delete(listener) },
    () => state,
  )
}
